import React, { useCallback, useMemo, useState } from "react";
import styles from "./index.module.scss";
import DetailLayout from "../CourseDetail/components/DetailLayout";
import DetailLayoutHeader from "../CourseDetail/components/DetailLayout/components/DetailLayoutHeader";
import { Spin } from "antd";
import ChaptersList from "./components/ChaptersList";
import useRequest from "Utils/useRequest";
import { getChapters, getResourceListFromChapter } from "API/courseware";
import useModalHandler from "Utils/useModalHandler";
import CoursewareModal from "../CourseDetail/Courseware/components/CoursewareModal";
import { isTrue } from "Utils/filter";
import { RouteComponentProps, useLocation } from "react-router-dom";
import { getCourseInfo } from "API";
import ChapterHeaderExtra from "./components/ChapterHeaderExtra";
import { ChapterContext } from "./store";
import useDeviceType from "../../Utils/useDeviceType";
import ChaptersListMobile from "./components/ChaptersListMobile";
import qs from "qs";
import { useAtomValue } from "jotai";
import { CourseListAtom } from "../CourseDetail/Store";

interface CourseChaptersProps {
    courseNo: string;
    courseSeq: string;
    weekTimeId?: string;
}

export default function CourseChapters({
    match
}: RouteComponentProps<CourseChaptersProps>) {
    const modal = useModalHandler();
    const { isPC } = useDeviceType();
    const location = useLocation();

    const { courseName } = useMemo(
        () => qs.parse(location.search, { ignoreQueryPrefix: true }) as any,
        [location.search]
    );

    const { courseNo, courseSeq, weekTimeId } = match.params;

    const [current, setCurrent] = useState("");

    const courseList = useAtomValue(
        CourseListAtom({
            courseNo: courseNo,
            courseSeq: courseSeq
        })
    );

    const [courseInfo] = useRequest(
        () => getCourseInfo(courseList, courseNo, courseSeq),
        { silence: true },
        [courseNo, courseSeq]
    );

    const [data, { loading }, refresh] = useRequest(
        () => getChapters(courseNo, courseSeq),
        {},
        [courseNo, courseSeq]
    );

    // 当前课程所有课件列表
    const resourceList = useMemo(
        () =>
            data?.sourceChapter
                ? getResourceListFromChapter(data?.sourceChapter)
                : [],
        [data]
    );

    const onViewCourseware = useCallback(
        (id: string) => {
            const item = resourceList.find(item => id === item.id);
            if (!item) return;
            modal.open();
            setCurrent(item.id);
        },
        [modal, resourceList]
    );

    // 未完成章节
    const todoResource = useMemo(
        () =>
            resourceList.find(
                item => !isTrue(item.studyStatusEntity?.studyStatus)
            ),
        [resourceList]
    );

    // 继续学习未完成章节
    const onContinue = useCallback(() => {
        if (!todoResource) return;
        onViewCourseware(todoResource.id);
    }, [onViewCourseware, todoResource]);

    return (
        <ChapterContext.Provider
            value={{
                resourceList,
                todoResource,
                onViewCourseware,
                onContinue
            }}>
            <DetailLayout
                breadcrumb="章节目录"
                mobileHeaderExtra={<ChapterHeaderExtra />}>
                {isPC && (
                    <DetailLayoutHeader
                        title={courseName || courseInfo?.courseName || ""}
                        extra={<ChapterHeaderExtra />}
                    />
                )}

                <Spin spinning={loading}>
                    <div className={styles.main}>
                        {data &&
                            (isPC ? (
                                <ChaptersList data={data} />
                            ) : (
                                <ChaptersListMobile data={data} />
                            ))}
                    </div>
                </Spin>

                <CoursewareModal
                    visible={modal.visible}
                    onCancel={modal.close}
                    onSelect={setCurrent}
                    onChange={refresh}
                    current={current}
                    list={resourceList}
                />
            </DetailLayout>
        </ChapterContext.Provider>
    );
}
